<template>
  <div>
    <LlAutocomplete
      :model-value="modelValue"
      :search="vendorSearch.searchTerm.value"
      :items="availableVendors"
      item-title="displayName"
      item-value="vendorId"
      :loading="vendorSearch.vendorsLoading.value"
      placeholder="Search vendors…"
      hide-details="auto"
      clearable
      :data-cy="dataCy"
      @update:model-value="(v) => $emit('update:modelValue', v ?? null)"
      @update:search="vendorSearch.search"
    >
      <!-- Renders the selected vendor's name directly from availableVendors rather than trusting
           Vuetify's own item lookup, which can show the raw vendorId if it hasn't matched yet. -->
      <template #selection>{{ selectedLabel }}</template>
      <template #no-data>
        <LlListItem
          :title="vendorSearch.vendorError.value || 'No vendors found'"
          :color="vendorSearch.vendorError.value ? 'error' : 'muted'"
        />
      </template>
      <template #append-item>
        <LlListItem
          icon="mdi-plus"
          color="primary"
          clickable
          :title="addNewVendorLabel"
          :data-cy="`${dataCy}-add-new-vendor`"
          @click="vendorSearch.openAddVendor()"
        />
      </template>
    </LlAutocomplete>

    <div v-if="vendorSearch.showAddVendor.value" class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
      <label class="text-xs font-medium text-gray-700 mb-1 block">Display Name <span class="font-bold">*</span></label>
      <div class="flex items-center gap-2">
        <LlTextField
          v-model="vendorSearch.newVendorName.value"
          placeholder="e.g., Smith & Associates"
          hide-details="auto"
          :data-cy="`${dataCy}-new-vendor-name`"
          class="flex-1"
        />
        <LlSecondaryButton color="primary" :loading="vendorSearch.creatingVendor.value" :data-cy="`${dataCy}-save-new-vendor`" @click="onCreateVendor">Create</LlSecondaryButton>
        <LlSecondaryButton :data-cy="`${dataCy}-cancel-new-vendor`" @click="vendorSearch.cancelAddVendor()">Cancel</LlSecondaryButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlAutocomplete from '@/components/LlAutocomplete.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlListItem from '@/components/LlListItem.vue';
import type { useVendorSearch } from './useVendorSearch';
import type { VendorData } from '@/models/lienTypes';

const props = defineProps<{
  modelValue: string | null;
  vendorSearch: ReturnType<typeof useVendorSearch>;
  availableVendors: VendorData[];
  dataCy: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const selectedLabel = computed(
  () => props.availableVendors.find((v) => v.vendorId === props.modelValue)?.displayName ?? '',
);

const addNewVendorLabel = computed(() => {
  const term = props.vendorSearch.searchTerm.value;
  return term ? `Create New "${term}"` : 'Add New Vendor';
});

/** Creates the vendor inline, then auto-selects it for the caller (mirrors LienFormPanel). */
async function onCreateVendor(): Promise<void> {
  const vendor = await props.vendorSearch.saveNewVendor();
  if (vendor) emit('update:modelValue', vendor.vendorId);
}
</script>
